/**
 * Configuración de runtime, leída de /config.json antes de montar la app.
 *
 * Antes vivía en un <script id="BV2Config"> dentro de index.html, sustituido por
 * un `sed` en CI ANTES del `npm run build`: la URL de la API y del IDP quedaban
 * horneadas en el bundle, así que cada ambiente necesitaba su propia
 * construcción, y las versiones de dev y de producción vivían en dos archivos
 * distintos que se desincronizaban.
 *
 * Vite copia public/ tal cual, sin hashear los nombres, así que /config.json se
 * puede reemplazar en el contenedor al arrancar (envsubst) y una sola imagen
 * sirve para todos los ambientes.
 */

export interface RuntimeConfig {
  oidc: {
    /** URL del realm, p. ej. http://localhost:8081/realms/banco */
    authority: string;
    client_id: string;
    scopes: string;
  };
  api: {
    /** Base de la API, sin barra final. */
    url: string;
  };
}

function assertShape(raw: unknown): RuntimeConfig {
  const cfg = raw as RuntimeConfig;
  const missing: string[] = [];
  if (!cfg?.oidc?.authority) missing.push("oidc.authority");
  if (!cfg?.oidc?.client_id) missing.push("oidc.client_id");
  if (!cfg?.api?.url) missing.push("api.url");
  if (missing.length) {
    throw new Error(`config.json incompleto: falta ${missing.join(", ")}`);
  }
  return cfg;
}

/**
 * Carga /config.json.
 *
 * Falla ruidosamente a propósito. Una configuración vacía produciría un
 * UserManager con `authority: ""` y un fallo de OIDC incomprensible más
 * adelante; es mejor no arrancar y decir por qué.
 */
export async function loadConfig(): Promise<RuntimeConfig> {
  const url = `${import.meta.env.BASE_URL}config.json`;
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`No se pudo leer ${url}: HTTP ${res.status}`);
  }
  return assertShape(await res.json());
}

/**
 * Pinta un error a pantalla completa cuando el arranque falla.
 *
 * Sin esto el usuario ve una página en blanco y el motivo queda sólo en la
 * consola.
 */
export function renderBootError(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  document.body.innerHTML = `
    <div style="font-family: Roboto, system-ui, sans-serif; max-width: 40rem;
                margin: 4rem auto; padding: 2rem; border-radius: .5rem;
                background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.12);">
      <h1 style="color:#862930; font-size:1.25rem; margin:0 0 .75rem;">
        No fue posible iniciar la aplicación
      </h1>
      <p style="color:#333; margin:0 0 1rem; line-height:1.5;">
        Revisa la configuración de <code>/config.json</code> y que la API y el
        proveedor de identidad estén disponibles.
      </p>
      <pre style="background:#f5f5f5; padding:.75rem; border-radius:.25rem;
                  overflow-x:auto; font-size:.8125rem; margin:0;">${message.replace(
                    /[<>&]/g,
                    (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" })[c] as string
                  )}</pre>
    </div>`;
}
